(ns manage.product-selector-result-table.view
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium GridOverlay useGridApiContext]]
            ["src/svg/artwork/not-found.svg$default" :as NotFound]
            ["src/svg/artwork/warning.svg$default" :as Warning]
            ["src/svg/shapes/rectangle/rectangle-split-3x1.svg$default" :as ColumnsIcon]
            ["src/svg/wpp/content-actions/edit.svg$default" :as Edit]
            [cljs-bean.core :refer [->clj ->js]]
            [common.patterns.loading-view :refer [loading-view]]
            [common.queries :refer [use-query]]
            [common.resource.query.product-selector :as product-selector.q]
            [common.widgets.data-grid :as dg]
            [malli.core :as m]
            [manage.product-selector-page.hooks :as hooks]
            [manage.product-table.view :as product-table]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defhook use-columns [{:keys [product-selector allow-product-overrides? on-edit]}]
  (let [required-fields (or (-> product-selector :query :required-fields) [])
        product-store-columns (some->> (product-table/use-product-store-columns)
                                       (sort-by (fn [^js column]
                                                  (let [index (.indexOf required-fields (.-field column))]
                                                    (if (neg? index)
                                                      js/Infinity
                                                      index)))))
        update-column [#js {:field "prd-edit-field"
                            :headerName ""
                            :width 120
                            :sortable false
                            :filterable false
                            :disableColumnMenu true
                            :visible true
                            :renderCell
                            (fn [^js params]
                              (let [row (-> params .-row ->clj)
                                    updated? (:overwritten-item row)
                                    selection-index (:index row)]
                                ($ :<>
                                   (when updated?
                                     ($ Chip {:label "Updated"}))
                                   ($ :div {:className "row-action"}
                                      ($ IconButton
                                         {:size "small"
                                          :on-click #(@on-edit selection-index)}
                                         ($ SvgIcon
                                            ($ Edit)))))))}]
        base_columns (some->> product-store-columns
                              (concat product-table/default-columns)
                              into-array)
        columns (cond-> base_columns
                  allow-product-overrides? (-> (concat update-column) into-array))]
    columns))

(def Toolbar
  (uix/as-react
   (fn []
     (let [api (-> (useGridApiContext) .-current)]
       ($ dg/grid-toolbar
          {:hide-quick-filter true}
          ($ Button
             {:endIcon ($ ColumnsIcon)
              :onClick #(.. api (showPreferences "columns"))}
             "Columns"))))))

(defn get-row-id [row] (:id row))

(def NoRowsOverlay
  (uix/as-react
   (fn [{:keys [valid-product-selector?]}]
     (let [{:keys [artwork title description]}
           (if (not valid-product-selector?)
             {:artwork Warning
              :title "Can’t load products"
              :description "There’s an issue with the information you entered.
                            Please fix any highlighted fields and try again."}
             {:artwork NotFound
              :title "No products match your filters"
              :description "Try adjusting your product selector settings to see more results."})]
       ($ GridOverlay
          ($ Stack
             {:gap 3
              :alignItems "center"}
             ($ artwork)
             ($ Stack
                {:gap 1
                 :alignItems "center"}
                ($ Typography {:variant "h3"} title)
                ($ :span description))))))))

(def product-override-fn (atom nil))

(defui product-selector-result-datagrid
  [{:keys [product-selector product-overrides allow-product-overrides? set-products-fn]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        columns (use-columns {:product-selector product-selector :on-edit product-override-fn
                              :allow-product-overrides? allow-product-overrides?})
        product-selector-id (:id product-selector)
        this-selector-product-overrides (get product-overrides product-selector-id (-> (repeat 10 nil) vec))

        ;; to work around state refreshing bug
        [local-overrides set-local-overrides] (uix/use-state (-> (repeat 10 nil) vec))

        effective-overrides (mapv #(if (some? %2) %2 %1) this-selector-product-overrides local-overrides)

        initial-visibility-model (uix/use-memo
                                  (fn []
                                    (some->> columns
                                             (map-indexed (fn [idx ^js col]
                                                            [(.-field col) (or (< idx 5)
                                                                               (= (.-field col) "prd-edit-field"))]))
                                             (into {})
                                             ->js))
                                  [columns])

        {{rows :items} :data isFetching :isFetching}

        (use-query (product-selector.q/product-selector-preview-query
                    {:advertiser-id advertiser-id
                     :product-selector (hooks/->external-representation product-selector)}))

        valid-product-selector? (some->> product-selector
                                         not-empty
                                         (m/validate hooks/schema))

        some-products-updated? (not-every? nil? effective-overrides)

        updated-rows (mapv #(if (and (some? %2) (not= %2 %1)) (assoc %2 :overwritten-item true) %1) rows effective-overrides)
        indexed-rows (mapv #(assoc %1 :index %2) updated-rows (range))

        override-fn (fn [selection-index]
                      (rf/dispatch [:modal/open
                                    {:id :product-modal
                                     :props {:on-select (fn [product]
                                                          (set-products-fn
                                                           product
                                                           (:id product-selector)
                                                           selection-index
                                                           (merge product-overrides
                                                                  {product-selector-id effective-overrides})
                                                           rows)
                                                          (set-local-overrides (assoc local-overrides selection-index product)))
                                             :disabled-products (to-array updated-rows)}}]))]
    (reset! product-override-fn override-fn)
    ($ Stack
       (when some-products-updated?
         ($ Alert
            {:severity "info"
             :sx #js {:bgcolor "grey.80"}}
            "This selection is shared across multiple ads, so any updates will apply to all variants using it."))
       (if-not initial-visibility-model
         ($ loading-view)

         ($ DataGridPremium
            {:columns columns

             :rows (to-array indexed-rows)
             :getRowId get-row-id

             :loading (and isFetching (not (false? valid-product-selector?)))

             :disableColumnFilter true
             :disableRowSelectionOnClick false
             :slots #js {:toolbar Toolbar
                         :noRowsOverlay NoRowsOverlay}
             :slotProps #js {:noRowsOverlay #js {:valid-product-selector? valid-product-selector?}}
             :initialState #js {:columns #js {:columnVisibilityModel initial-visibility-model}}
             :getRowClassName (fn [^js params]
                                (if (:overwritten-item (.-row params))
                                  "row--changed"
                                  ""))
             :sx #js {"& .MuiDataGrid-row.row--changed" #js {:backgroundColor "rgb(255, 249, 217)"}
                      :minHeight "60rem"
                      ".row-action" #js {:visibility "hidden"}
                      ".MuiDataGrid-row:hover .row-action" #js {:visibility "visible"}}})))))
